// features/workstations/dashboard-sections/gex-levels.js
// ═══════════════════════════════════════════════════════════
// GEX + DEALER LEVELS — FlashAlpha Lab API surface
// ═══════════════════════════════════════════════════════════
// Phase 1 widget validating the FlashAlpha integration. Shows:
//   - Symbol tabs (SPY / QQQ / TSLA by default)
//   - Current price (from /stockquote — Free tier)
//   - GEX by strike bar chart (from /v1/exposure/gex — Free
//     for stocks, Basic+ for ETFs)
//   - Technical levels (from /v1/exposure/levels)
//   - Max pain (if Basic+)
//   - Narrative (if Growth+)
//   - Tier-gated fields surface their 403 message as
//     "upgrade to unlock" hints.
//
// User-triggered refresh only — no cron on Free tier since
// quota is 5/day. Cache window 60 min.
// ═══════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Eyebrow,
  SerifHeadline,
  SerifSummary,
  SectionStrip,
  PractitionerLens,
  MetaField,
} from '../../../components/widgets/primitives';

const DEFAULT_SYMBOLS = ['TSLA', 'SPY', 'QQQ'];

// ═══════════════════════════════════════════════════════════
// SECTION
// ═══════════════════════════════════════════════════════════
export default function GexLevelsSection({ workstation }) {
  const [symbol, setSymbol] = useState(DEFAULT_SYMBOLS[0]);
  const [snapshots, setSnapshots] = useState({}); // keyed by symbol
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSymbol = useCallback(async (sym, { force } = {}) => {
    if (!sym) return;
    setLoading(true);
    setError(null);
    try {
      const qs = new URLSearchParams({ symbol: sym });
      if (force) qs.set('refresh', '1');
      const res = await fetch(`/api/intelligence/flashalpha?${qs}`, { cache: 'no-store' });
      const payload = res.ok ? await res.json() : null;
      if (!res.ok) setError(payload?.error || `HTTP ${res.status}`);
      setSnapshots((prev) => ({ ...prev, [sym]: payload }));
    } catch (e) {
      setError(e?.message || 'Load failed');
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-load selected symbol's cache on mount / symbol change
  useEffect(() => {
    if (!snapshots[symbol]) fetchSymbol(symbol);
  }, [symbol, snapshots, fetchSymbol]);

  const current = snapshots[symbol];
  const snapshot = current?.snapshot || null;
  const ageMin = current?.age_min ?? null;
  const cacheHit = current?.cache_hit;

  return (
    <section className="ws-gex">
      <Eyebrow parts={[
        'gex + dealer levels',
        'flashalpha',
        snapshot ? inferTierLabel(snapshot.tier) : null,
        cacheHit && ageMin != null ? `${ageMin}m cache` : null,
      ]} />

      <SerifHeadline>{headlineFor(symbol, snapshot)}</SerifHeadline>
      <SerifSummary>{summaryFor(snapshot)}</SerifSummary>

      {/* Symbol tabs + refresh */}
      <div className="ws-gex-tabs">
        {DEFAULT_SYMBOLS.map((s) => (
          <button
            key={s}
            type="button"
            className={`ws-gex-tab ${s === symbol ? 'is-active' : ''}`}
            onClick={() => setSymbol(s)}
          >
            {s}
          </button>
        ))}
        <button
          type="button"
          className="ws-gex-refresh"
          onClick={() => fetchSymbol(symbol, { force: true })}
          disabled={loading}
          title="Force refresh — consumes 1 API quota"
        >
          {loading ? 'fetching…' : '↻ refresh'}
        </button>
      </div>

      {loading && !snapshot && (
        <div className="ws-gex-loading">Loading {symbol}…</div>
      )}

      {!loading && !snapshot && (
        <div className="ws-gex-empty">
          No snapshot yet for {symbol}. Click <em>refresh</em> to fetch.
        </div>
      )}

      {snapshot && (
        <>
          {/* GEX top-line stats — underlying_price + net_gex + gamma_flip
              + gex_interpretation all come inline on the GEX response.
              Fall back to levels response if GEX endpoint didn't return. */}
          {snapshot.gex
            ? <GexHeadline gex={snapshot.gex} />
            : snapshot.levels
              ? <GexHeadline gex={headlineFromLevels(snapshot.levels)} />
              : null}

          {/* Stockquote strip */}
          {snapshot.stockquote && (
            <StockquotePanel data={snapshot.stockquote} />
          )}

          {/* GEX + Levels */}
          <GexAndLevels
            gex={snapshot.gex}
            levels={snapshot.levels}
            error={snapshot.endpoint_errors?.gex || snapshot.endpoint_errors?.levels}
          />

          {/* Max pain */}
          {(snapshot.maxpain || snapshot.endpoint_errors?.maxpain) && (
            <MaxPainPanel
              data={snapshot.maxpain}
              error={snapshot.endpoint_errors?.maxpain}
            />
          )}

          {/* Narrative */}
          {(snapshot.narrative || snapshot.endpoint_errors?.narrative) && (
            <NarrativePanel
              data={snapshot.narrative}
              error={snapshot.endpoint_errors?.narrative}
            />
          )}

          {/* Zero-DTE */}
          {(snapshot.zero_dte || snapshot.endpoint_errors?.zero_dte) && (
            <ZeroDtePanel
              data={snapshot.zero_dte}
              error={snapshot.endpoint_errors?.zero_dte}
            />
          )}

          {/* Freshness footer */}
          <MetaField label="as of">
            {fmtDateTime(snapshot.as_of)}
            {cacheHit && ageMin != null && ` · cached ${ageMin}m ago`}
          </MetaField>

          {current?.rate_limit && current.rate_limit.remaining != null && (
            <MetaField label="flashalpha quota">
              {current.rate_limit.remaining} / {current.rate_limit.limit} remaining today
            </MetaField>
          )}
        </>
      )}

      {error && <div className="ws-gex-error">{error}</div>}

      <PractitionerLens label="how to read this">
        Free tier gives you GEX on individual stocks (TSLA works);
        ETFs (SPY / QQQ) require Basic+ to unlock the GEX and max
        pain endpoints. The widget shows what’s accessible on your
        current key and surfaces each tier-gated endpoint as an
        upgrade hint — so you can see exactly what flipping to
        Basic would unlock before you spend the $79.
      </PractitionerLens>
    </section>
  );
}

// ═══════════════════════════════════════════════════════════
// GEX HEADLINE — underlying price + net_gex + gamma_flip +
// interpretation, all pulled from the GEX endpoint response.
// ═══════════════════════════════════════════════════════════
function GexHeadline({ gex }) {
  const underlying = pickNumber(gex, ['underlying_price']);
  const netGex = pickNumber(gex, ['net_gex']);
  const gammaFlip = pickNumber(gex, ['gamma_flip']);
  const interpretation = typeof gex?.gex_interpretation === 'string' ? gex.gex_interpretation : null;

  const netGexTone = netGex == null ? 'steel'
    : netGex > 0 ? 'sage' : 'terra';

  // Above gamma flip = dealers dampen (positive gamma regime)
  // Below gamma flip = dealers amplify (negative gamma regime)
  const flipRelation = (underlying != null && gammaFlip != null)
    ? (underlying >= gammaFlip ? 'above' : 'below')
    : null;

  return (
    <div className={`ws-gex-head ws-gex-head-${netGexTone}`}>
      <div className="ws-gex-head-top">
        {underlying != null && (
          <div className="ws-gex-head-price">
            ${underlying.toFixed(2)}
          </div>
        )}
        <div className="ws-gex-head-stats">
          {netGex != null && (
            <div className={`ws-gex-head-stat ws-gex-head-stat-${netGexTone}`}>
              <span className="ws-gex-head-stat-label">net gex</span>
              <span className="ws-gex-head-stat-value">{fmtGex(netGex)}</span>
            </div>
          )}
          {gammaFlip != null && (
            <div className="ws-gex-head-stat">
              <span className="ws-gex-head-stat-label">gamma flip</span>
              <span className="ws-gex-head-stat-value">${gammaFlip.toFixed(2)}</span>
              {flipRelation && (
                <span className={`ws-gex-head-stat-tag ws-gex-head-stat-tag-${flipRelation === 'above' ? 'sage' : 'terra'}`}>
                  {flipRelation}
                </span>
              )}
            </div>
          )}
        </div>
      </div>
      {interpretation && (
        <div className="ws-gex-head-interpretation">{interpretation}</div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
// STOCKQUOTE PANEL
// ═══════════════════════════════════════════════════════════
function StockquotePanel({ data }) {
  // FlashAlpha stockquote shape is unknown to us at integration
  // time; render defensively by showing known fields + raw fallback.
  const price = pickNumber(data, ['price', 'last', 'lastPrice', 'close', 'c']);
  const change = pickNumber(data, ['change', 'change_dollar', 'chg']);
  const changePct = pickNumber(data, ['change_percent', 'changePercent', 'change_pct', 'chg_pct']);
  const volume = pickNumber(data, ['volume', 'vol', 'v']);
  const timestamp = data?.timestamp || data?.t || data?.updated_at || null;

  const changeTone = change == null ? 'steel' : change > 0 ? 'sage' : change < 0 ? 'terra' : 'steel';

  return (
    <SectionStrip label="stockquote">
      <div className="ws-gex-quote">
        <div className="ws-gex-quote-price">
          {price != null ? `$${price.toFixed(2)}` : '—'}
        </div>
        <div className={`ws-gex-quote-change ws-gex-quote-change-${changeTone}`}>
          {change != null ? `${change >= 0 ? '+' : ''}${change.toFixed(2)}` : '—'}
          {changePct != null && ` (${changePct >= 0 ? '+' : ''}${changePct.toFixed(2)}%)`}
        </div>
        {volume != null && (
          <div className="ws-gex-quote-volume">
            vol {fmtLargeInt(volume)}
          </div>
        )}
        {timestamp && (
          <div className="ws-gex-quote-time">as of {fmtDateTime(timestamp)}</div>
        )}
      </div>
    </SectionStrip>
  );
}

// ═══════════════════════════════════════════════════════════
// GEX + LEVELS PANEL
// ═══════════════════════════════════════════════════════════
function GexAndLevels({ gex, levels, error }) {
  const gexStrikes = extractGexStrikes(gex);
  const keyLevels = extractLevels(levels);

  if (!gex && !levels && error) {
    return (
      <SectionStrip label="gex + levels — locked">
        <div className="ws-gex-locked">
          <strong>Tier-gated.</strong> {error}
        </div>
      </SectionStrip>
    );
  }

  return (
    <SectionStrip label="gex + dealer levels">
      {keyLevels.length > 0 && (
        <div className="ws-gex-levels-grid">
          {keyLevels.map((lvl, i) => (
            <div key={i} className={`ws-gex-level ws-gex-level-${lvl.tone}`}>
              <div className="ws-gex-level-label">{lvl.label}</div>
              <div className={`ws-gex-level-value ws-gex-level-value-${lvl.tone}`}>
                {lvl.value != null ? `$${Number(lvl.value).toFixed(2)}` : '—'}
              </div>
            </div>
          ))}
        </div>
      )}

      {gexStrikes.length > 0 && (
        <div className="ws-gex-strikes">
          <div className="ws-gex-strikes-header">
            <span>strike</span>
            <span>gamma exposure</span>
          </div>
          {gexStrikes.map((row, i) => (
            <div key={i} className="ws-gex-strike-row">
              <span className="ws-gex-strike-price">${row.strike.toFixed(2)}</span>
              <div className="ws-gex-strike-bar-cell">
                <div
                  className={`ws-gex-strike-bar ws-gex-strike-bar-${row.sign}`}
                  style={{ width: `${Math.min(100, row.barPct)}%` }}
                />
                <span className="ws-gex-strike-value">{fmtGex(row.gex)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {gexStrikes.length === 0 && keyLevels.length === 0 && (gex || levels) && (
        <div className="ws-gex-raw">
          {/* Unknown response shape — show JSON for debugging */}
          <pre className="ws-gex-raw-pre">{JSON.stringify(gex || levels, null, 2).slice(0, 1000)}</pre>
        </div>
      )}
    </SectionStrip>
  );
}

// ═══════════════════════════════════════════════════════════
// MAX PAIN PANEL
// ═══════════════════════════════════════════════════════════
function MaxPainPanel({ data, error }) {
  if (!data && error) {
    return (
      <SectionStrip label="max pain — locked">
        <div className="ws-gex-locked">
          <strong>Requires Basic tier+.</strong> {error}
        </div>
      </SectionStrip>
    );
  }
  if (!data) return null;
  const maxPain = pickNumber(data, ['max_pain', 'maxPain', 'max_pain_strike', 'maxPainStrike']);
  const expiration = data?.expiration || data?.expiry || null;
  return (
    <SectionStrip label="max pain">
      <div className="ws-gex-maxpain">
        <div className="ws-gex-maxpain-value">
          {maxPain != null ? `$${maxPain.toFixed(2)}` : '—'}
        </div>
        {expiration && (
          <div className="ws-gex-maxpain-exp">for {expiration}</div>
        )}
      </div>
    </SectionStrip>
  );
}

// ═══════════════════════════════════════════════════════════
// NARRATIVE PANEL
// ═══════════════════════════════════════════════════════════
function NarrativePanel({ data, error }) {
  if (!data && error) {
    return (
      <SectionStrip label="narrative — locked">
        <div className="ws-gex-locked">
          <strong>Requires Growth tier+.</strong> {error}
        </div>
      </SectionStrip>
    );
  }
  if (!data) return null;
  const text = typeof data === 'string' ? data
    : data?.narrative || data?.text || data?.analysis || JSON.stringify(data).slice(0, 500);
  return (
    <SectionStrip label="narrative">
      <div className="ws-gex-narrative">{text}</div>
    </SectionStrip>
  );
}

// ═══════════════════════════════════════════════════════════
// ZERO-DTE PANEL
// ═══════════════════════════════════════════════════════════
function ZeroDtePanel({ data, error }) {
  if (!data && error) {
    return (
      <SectionStrip label="0DTE exposure — unavailable">
        <div className="ws-gex-locked">{error}</div>
      </SectionStrip>
    );
  }
  if (!data) return null;
  // Unknown exact shape — show known + raw fallback
  return (
    <SectionStrip label="0DTE exposure">
      <div className="ws-gex-raw">
        <pre className="ws-gex-raw-pre">{JSON.stringify(data, null, 2).slice(0, 800)}</pre>
      </div>
    </SectionStrip>
  );
}

// ─── Helpers: defensive shape parsing ───
function pickNumber(obj, keys) {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
    if (typeof v === 'string') {
      const n = Number(v);
      if (Number.isFinite(n)) return n;
    }
  }
  return null;
}

function extractGexStrikes(gex) {
  // FlashAlpha confirmed shape:
  //   { symbol, underlying_price, as_of, net_gex (total), gamma_flip,
  //     gex_interpretation, strikes: [{ strike, call_gex, put_gex, net_gex }] }
  if (!gex) return [];
  let rows = [];
  if (Array.isArray(gex)) rows = gex;
  else if (Array.isArray(gex.strikes)) rows = gex.strikes;
  else if (Array.isArray(gex.gex)) rows = gex.gex;
  else if (Array.isArray(gex.data)) rows = gex.data;
  else if (Array.isArray(gex.by_strike)) rows = gex.by_strike;
  if (rows.length === 0) return [];

  const normalized = rows
    .map((r) => {
      const strike = pickNumber(r, ['strike', 'k', 'price']);
      // FlashAlpha uses `net_gex` per strike; fall back to common
      // alternates + a call_gex + put_gex sum if net not present.
      let gxp = pickNumber(r, ['net_gex', 'gex', 'gamma_exposure', 'gamma', 'value', 'exposure']);
      if (gxp == null) {
        const call = pickNumber(r, ['call_gex']);
        const put = pickNumber(r, ['put_gex']);
        if (call != null || put != null) gxp = (call || 0) + (put || 0);
      }
      if (strike == null || gxp == null) return null;
      const call = pickNumber(r, ['call_gex']);
      const put = pickNumber(r, ['put_gex']);
      return { strike, gex: gxp, call_gex: call, put_gex: put };
    })
    .filter(Boolean);

  if (normalized.length === 0) return [];

  const maxAbs = Math.max(...normalized.map(r => Math.abs(r.gex))) || 1;
  return normalized
    .map((r) => ({
      ...r,
      sign: r.gex > 0 ? 'pos' : r.gex < 0 ? 'neg' : 'flat',
      barPct: (Math.abs(r.gex) / maxAbs) * 100,
    }))
    .sort((a, b) => a.strike - b.strike);
}

function extractLevels(levels) {
  if (!levels) return [];
  // FlashAlpha confirmed shape:
  //   { symbol, underlying_price, as_of, levels: {
  //       put_wall, call_wall, gamma_flip, zero_dte_magnet,
  //       highest_oi_strike, max_negative_gamma, max_positive_gamma
  //     } }
  // The level fields are NESTED under `levels.levels`. Unwrap before parsing.
  const payload = (levels.levels && typeof levels.levels === 'object')
    ? levels.levels
    : levels;

  const out = [];
  const candidates = [
    { key: 'gamma_flip',         label: 'gamma flip',    tone: 'gold' },
    { key: 'gammaFlip',          label: 'gamma flip',    tone: 'gold' },
    { key: 'zero_gamma',         label: 'zero gamma',    tone: 'gold' },
    { key: 'call_wall',          label: 'call wall',     tone: 'sage' },
    { key: 'callWall',           label: 'call wall',     tone: 'sage' },
    { key: 'put_wall',           label: 'put wall',      tone: 'terra' },
    { key: 'putWall',            label: 'put wall',      tone: 'terra' },
    { key: 'zero_dte_magnet',    label: '0DTE magnet',   tone: 'purple' },
    { key: 'highest_oi_strike',  label: 'highest OI',    tone: 'steel' },
    { key: 'max_positive_gamma', label: 'max +gamma',    tone: 'sage' },
    { key: 'max_negative_gamma', label: 'max −gamma',    tone: 'terra' },
    { key: 'support',            label: 'support',       tone: 'sage' },
    { key: 'resistance',         label: 'resistance',    tone: 'terra' },
    { key: 'max_gex_strike',     label: 'max gex',       tone: 'steel' },
    { key: 'maxGexStrike',       label: 'max gex',       tone: 'steel' },
  ];
  for (const c of candidates) {
    const v = pickNumber(payload, [c.key]);
    if (v != null) out.push({ label: c.label, value: v, tone: c.tone });
  }
  return out;
}

// If GEX endpoint didn't return but levels did, synthesize a
// minimal headline from the levels response so the user still
// gets underlying_price + gamma_flip surfaced.
function headlineFromLevels(levels) {
  if (!levels || typeof levels !== 'object') return null;
  const underlying = pickNumber(levels, ['underlying_price']);
  const payload = (levels.levels && typeof levels.levels === 'object') ? levels.levels : levels;
  const gammaFlip = pickNumber(payload, ['gamma_flip', 'gammaFlip']);
  if (underlying == null && gammaFlip == null) return null;
  return { underlying_price: underlying, gamma_flip: gammaFlip };
}

function inferTierLabel(tier) {
  if (!tier) return null;
  if (tier === 'growth_or_alpha') return 'growth+';
  if (tier === 'basic') return 'basic';
  if (tier === 'free')  return 'free';
  return tier;
}

function headlineFor(symbol, snapshot) {
  if (!snapshot) return `${symbol} — fetch to load options exposure.`;
  const tierLabel = inferTierLabel(snapshot.tier);
  return `${symbol} options exposure · ${tierLabel || 'tier unknown'}.`;
}

function summaryFor(snapshot) {
  if (!snapshot) {
    return 'FlashAlpha Lab API surfaces gamma / delta / vanna / charm exposure by strike, max pain, and dealer positioning. Free tier covers individual stocks; ETFs require Basic+.';
  }
  const hasGex = !!snapshot.gex;
  const hasLevels = !!snapshot.levels;
  const hasMaxPain = !!snapshot.maxpain;
  if (!hasGex && !hasLevels && !hasMaxPain) {
    return 'This symbol has no options exposure data accessible on the current tier. See upgrade hints below.';
  }
  return 'GEX, technical levels, and max pain from options flow positioning. Values reflect dealer hedging load at each strike.';
}

function fmtGex(n) {
  if (n == null || Number.isNaN(n)) return '—';
  const sign = n >= 0 ? '+' : '−';
  const abs = Math.abs(n);
  if (abs >= 1_000_000_000) return `${sign}${(abs / 1e9).toFixed(2)}B`;
  if (abs >= 1_000_000)     return `${sign}${(abs / 1e6).toFixed(2)}M`;
  if (abs >= 1_000)         return `${sign}${(abs / 1e3).toFixed(1)}k`;
  return `${sign}${abs.toFixed(0)}`;
}

function fmtLargeInt(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(2)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return String(Math.round(n));
}

function fmtDateTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-US', {
      month: 'short', day: 'numeric',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch { return ''; }
}
